import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Entity, generateEntityId } from '../entities/entity';
import { WA_PHYSICS, type Vector2 } from '../core/constants';
import { getGameRNG } from '../core/rng';

export const MINE_CONSTANTS = {
  DUD_CHANCE: 0.02,
  TRIGGER_DELAY: 0.5,
  ARM_DELAY: 2,
  TRIGGER_RADIUS: 30,
  EXPLOSION_RADIUS: 45,
  MAX_DAMAGE: 50,
} as const;

export class Mine implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2 = { x: 0, y: 0 };
  active = true;
  isGrounded = false;

  armed = false;
  armDelay = MINE_CONSTANTS.ARM_DELAY;
  armTimer = 0;
  triggerRadius = MINE_CONSTANTS.TRIGGER_RADIUS;
  explosionRadius = MINE_CONSTANTS.EXPLOSION_RADIUS;
  maxDamage = MINE_CONSTANTS.MAX_DAMAGE;
  ownerId: string;

  triggered = false;
  triggerTimer = 0;
  isDud = false;
  dudChance = MINE_CONSTANTS.DUD_CHANCE;

  onExplode?: (x: number, y: number) => void;
  onArmed?: () => void;
  onTriggered?: () => void;
  onDud?: () => void;

  constructor(x: number, y: number, ownerId: string) {
    this.id = generateEntityId('mine');
    this.position = { x, y };
    this.ownerId = ownerId;
    this.isDud = getGameRNG().next() < this.dudChance;
  }

  update(deltaTime: number): void {
    if (!this.active) return;

    if (!this.armed) {
      const wasArmed = this.armTimer >= this.armDelay;
      this.armTimer += deltaTime;
      if (!wasArmed && this.armTimer >= this.armDelay) {
        this.armed = true;
        this.onArmed?.();
      }
    }

    if (this.triggered) {
      this.triggerTimer += deltaTime;
      if (this.triggerTimer >= MINE_CONSTANTS.TRIGGER_DELAY) {
        if (this.isDud) {
          this.onDud?.();
          this.destroy();
        } else {
          this.explode();
        }
      }
    }

    if (!this.isGrounded) {
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
    }
  }

  applyTerrainPhysics(terrain: {
    isSolid(x: number, y: number): boolean;
    getSurfaceNormal(x: number, y: number): Vector2;
  }): void {
    if (!this.active) return;

    if (this.isGrounded) {
      const hasGround =
        terrain.isSolid(this.position.x - 3, this.position.y + 1) ||
        terrain.isSolid(this.position.x, this.position.y + 1) ||
        terrain.isSolid(this.position.x + 3, this.position.y + 1);
      if (!hasGround) {
        this.isGrounded = false;
      }
      return;
    }

    if (terrain.isSolid(this.position.x, this.position.y)) {
      const normal = terrain.getSurfaceNormal(this.position.x, this.position.y);

      const dot = this.velocity.x * normal.x + this.velocity.y * normal.y;
      this.velocity.x = (this.velocity.x - 2 * dot * normal.x) * 0.2;
      this.velocity.y = (this.velocity.y - 2 * dot * normal.y) * 0.15;

      this.position.x += normal.x * 2;
      this.position.y += normal.y * 2;

      const speed = Math.sqrt(
        this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y,
      );
      if (speed < 15) {
        this.velocity.x = 0;
        this.velocity.y = 0;
        this.isGrounded = true;
      }
    }
  }

  applyExplosionForce(pushX: number, pushY: number): void {
    this.velocity.x += pushX;
    this.velocity.y += pushY;
    this.isGrounded = false;
  }

  checkTrigger(worms: { id: string; position: Vector2; state: string }[]): boolean {
    if (!this.armed || !this.active || this.triggered) return false;

    for (const worm of worms) {
      if (worm.state === 'dead') continue;

      const dx = worm.position.x - this.position.x;
      const dy = worm.position.y - this.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < this.triggerRadius) {
        this.trigger();
        return false;
      }
    }

    return false;
  }

  trigger(): void {
    if (this.triggered || !this.active) return;
    this.triggered = true;
    this.triggerTimer = 0;
    this.onTriggered?.();
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x, this.position.y);
  }

  destroy(): void {
    this.active = false;
  }
}

export const WeaponMine: WeaponDefinition = {
  id: 'mine',
  name: 'Mine',
  icon: 'mine.png',
  category: 'thrown',
  maxAmmo: 2,
  maxUsesPerTurn: 1,
  powerCharging: false,
  timerAdjustable: false,
  showAimGuide: false,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm } = ctx;

    const dropX = worm.position.x + (worm.facing === 'right' ? 20 : -20);
    const dropY = worm.position.y + worm.getBounds().height;

    const mine = new Mine(dropX, dropY, worm.id);
    mine.velocity.x = worm.velocity.x * 0.5;
    mine.velocity.y = Math.max(worm.velocity.y, 0);

    return {
      success: true,
      entities: [mine],
      message: 'Mine placed',
    };
  },
};
